import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';

import { getAllProperties } from '../PropertyModule/shared/services/data.service';
import { UserRole } from '../shared/enums/user-role.enum';
import { UserType } from '../shared/enums/user-type.enum';
import { searchUsers } from '../shared/services/data/user.data';
import { getAllUnitTypes } from '../shared/services/data/unit-type.data';
import { hasRole } from '../shared/services/helpers/auth-helper/auth-helper.service';
import { PropertyUiModel } from '../shared/ui-models/property.ui-model';
import { UnitTypeUiModelResponse } from '../shared/ui-models/unit-type.ui-model';
import { UserUIModel } from '../shared/ui-models/user.ui-model';
import HouseKeepingAssignModal from './HouseKeepingAssignModal';
import HouseKeepingCreateModal from './HouseKeepingCreateModal';
import HousekeepingOrderCardCompact from './HousekeepingOrderCardCompact';
import { HouseKeepingInspectionModal } from './HouseKeepingInspectionModal';
import {
  assignHousekeepingOrderToSelf,
  assignHousekeepingOrderUsers,
  assignHousekeepingUsers,
  getHousekeepingOrders,
  updateHousekeepingOrderStatus,
  uploadHousekeepingOrderImages
} from './data.service';
import {
  HousekeepingAssigneeOption,
  HouseKeepingInspectionModalState,
  HousekeepingOrderItem,
  HousekeepingOrderSearchQuery
} from './types';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import { birdToast } from '../shared/components/core-ui/toast';

const MAX_IMAGE_WIDTH = 1600;
const MAX_IMAGE_HEIGHT = 1600;
const IMAGE_QUALITY = 0.6;

const optimizeImage = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/')) return file;

  const imageUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageUrl;
    });

    const scale = Math.min(MAX_IMAGE_WIDTH / image.width, MAX_IMAGE_HEIGHT / image.height, 1);
    const targetWidth = Math.round(image.width * scale);
    const targetHeight = Math.round(image.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext('2d');
    if (!context) return file;

    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const optimizedBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', IMAGE_QUALITY);
    });

    if (!optimizedBlob || optimizedBlob.size >= file.size) {
      return file;
    }

    const fileName = file.name.replace(/\.[^.]+$/, '.jpg');
    return new File([optimizedBlob], fileName, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};

const HouseKeepingContainer = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [orders, setOrders] = useState<HousekeepingOrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [project, setProject] = useState('');
  const [unit, setUnit] = useState('');
  const [projectOptions, setProjectOptions] = useState<
    { label: string; value: string; areaId?: string }[]
  >([]);
  const [unitOptions, setUnitOptions] = useState<
    { label: string; value: string; projectId?: string }[]
  >([]);
  const [housekeepingOptions, setHousekeepingOptions] = useState<HousekeepingAssigneeOption[]>([]);
  const [selectedAssigneesByOrder, setSelectedAssigneesByOrder] = useState<
    Record<string, string[]>
  >({});
  const [savingAssignmentsByOrder, setSavingAssignmentsByOrder] = useState<Record<string, boolean>>(
    {}
  );
  const [assignmentOrder, setAssignmentOrder] = useState<HousekeepingOrderItem | null>(null);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignProject, setBulkAssignProject] = useState('');
  const [bulkAssignUnits, setBulkAssignUnits] = useState<string[]>([]);
  const [bulkAssignUserId, setBulkAssignUserId] = useState('');
  const [bulkAssignSaving, setBulkAssignSaving] = useState(false);
  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [orderDateFrom, setOrderDateFrom] = useState<Date | null>(null);
  const [orderDateTo, setOrderDateTo] = useState<Date | null>(null);
  const [offset, setOffset] = useState(1);
  const [limit] = useState(20);
  const [count, setCount] = useState(0);
  const [inspectionModal, setInspectionModal] = useState<HouseKeepingInspectionModalState | null>(
    null
  );

  const canViewFilters = hasRole([
    UserRole.OPERATION_MANAGER,
    UserRole.HOUSE_KEEPING_MANAGER,
    UserRole.GES,
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN
  ]);

  const canManageAssignments = canViewFilters;

  const housekeepingOptionsById = useMemo(
    () =>
      housekeepingOptions.reduce<Record<string, HousekeepingAssigneeOption>>((acc, option) => {
        acc[option.value] = option;
        return acc;
      }, {}),
    [housekeepingOptions]
  );

  const bulkProjectOptions = useMemo(
    () => projectOptions.map((option) => ({ label: option.label, value: option.value })),
    [projectOptions]
  );

  const bulkUnitOptions = useMemo(
    () =>
      unitOptions
        .filter(
          (option) =>
            !bulkAssignProject || String(option.projectId || '') === String(bulkAssignProject)
        )
        .map((option) => ({ label: option.label, value: option.value })),
    [bulkAssignProject, unitOptions]
  );

  const handleToggleCard = (id: string) => {
    setExpandedId((currentId) => (currentId === id ? null : id));
  };

  const syncSelectedAssignees = useCallback((ordersList: HousekeepingOrderItem[]) => {
    setSelectedAssigneesByOrder((current) => {
      const next = { ...current };
      ordersList.forEach((order) => {
        if (!next[order.id]) {
          next[order.id] = [];
        }
      });
      return next;
    });
  }, []);

  const handleOrderUpdate = (order: HousekeepingOrderItem) => {
    const currentStatus = order.status || 'new';

    if (currentStatus === 'done') {
      return;
    }

    if (currentStatus === 'in_progress') {
      setInspectionModal({
        orderId: order.id,
        bedroom1Photo: null,
        bedroom2Photo: null,
        bathroomPhoto: null,
        kitchenPhoto: null,
        livingAreaPhoto: null
      });
      return;
    }

    updateHousekeepingOrderStatus(order.id, 'in_progress').then((response) => {
      if (!isErrorInfo(response)) {
        birdToast.success('تم تحديث الحالة إلى قيد التنفيذ');
        loadOrders(offset);
      } else {
        birdToast.error('حدث خطأ أثناء تحديث الحالة. حاول مرة أخرى.');
      }
    });
  };

  const handleInspectionPhotoChange = (
    photoType: keyof Omit<HouseKeepingInspectionModalState, 'orderId'>,
    file: File | null
  ) => {
    setInspectionModal((prev) =>
      prev
        ? {
            ...prev,
            [photoType]: file
          }
        : prev
    );
  };

  const handleConfirmDone = () => {
    if (!inspectionModal) return;

    const { orderId, bedroom1Photo, bedroom2Photo, bathroomPhoto, kitchenPhoto, livingAreaPhoto } =
      inspectionModal;
    if (!bedroom1Photo || !bedroom2Photo || !bathroomPhoto || !kitchenPhoto || !livingAreaPhoto)
      return;

    const promise = Promise.all([
      optimizeImage(bedroom1Photo),
      optimizeImage(bedroom2Photo),
      optimizeImage(bathroomPhoto),
      optimizeImage(kitchenPhoto),
      optimizeImage(livingAreaPhoto)
    ])
      .then(
        ([
          optimizedBedroom1Photo,
          optimizedBedroom2Photo,
          optimizedBathroomPhoto,
          optimizedKitchenPhoto,
          optimizedLivingAreaPhoto
        ]) =>
          uploadHousekeepingOrderImages(orderId, {
            bedroom1Photo: optimizedBedroom1Photo,
            bedroom2Photo: optimizedBedroom2Photo,
            bathroomPhoto: optimizedBathroomPhoto,
            kitchenPhoto: optimizedKitchenPhoto,
            livingAreaPhoto: optimizedLivingAreaPhoto
          })
      )
      .then((uploadResult) => {
        if (isErrorInfo(uploadResult)) {
          throw uploadResult;
        }

        loadOrders(offset);
        setInspectionModal(null);
      });

    birdToast.promise(promise, {
      loadingMessage: 'جاري رفع صور التنظيف...',
      successMessage: 'تم إنهاء مهمة التنظيف بنجاح',
      errorFailedMessage: 'فشل إنهاء مهمة التنظيف'
    });
  };

  const handleAssignSelf = (id: string) => {
    assignHousekeepingOrderToSelf(id).then((response) => {
      if (!isErrorInfo(response)) {
        birdToast.success('تم إسناد الطلب لنفسك بنجاح');
        loadOrders(offset);
      } else {
        birdToast.error('حدث خطأ أثناء إسناد الطلب لنفسك. حاول مرة أخرى.');
      }
    });
  };

  const loadOrders = useCallback(
    (nextOffset = offset, overrideProject?: string, overrideUnit?: string) => {
      setLoading(true);

      const queryProject = overrideProject !== undefined ? overrideProject : project;
      const queryUnit = overrideUnit !== undefined ? overrideUnit : unit;

      const query: HousekeepingOrderSearchQuery = {
        offset: nextOffset,
        limit,
        ...(canViewFilters && queryProject ? { project: queryProject } : {}),
        ...(canViewFilters && queryUnit ? { unit: queryUnit } : {}),
        ...(canViewFilters && orderDateFrom ? { orderDateFrom: orderDateFrom.getTime() } : {}),
        ...(canViewFilters && orderDateTo ? { orderDateTo: orderDateTo.getTime() } : {})
      };

      getHousekeepingOrders(query).then((response) => {
        setLoading(false);

        if (isErrorInfo(response)) {
          setLoadError(true);
          setOrders([]);
          setCount(0);
          return;
        }

        setLoadError(false);
        setOrders(response.data);
        setCount(response.count);
        syncSelectedAssignees(response.data);
        setExpandedId((currentId) => currentId ?? response.data[0]?.id ?? null);
      });
    },
    [
      canViewFilters,
      limit,
      offset,
      orderDateFrom,
      orderDateTo,
      project,
      syncSelectedAssignees,
      unit
    ]
  );

  const loadHousekeepingUsers = useCallback(() => {
    if (!canManageAssignments) {
      return;
    }

    searchUsers(
      {
        role: [UserType.HOUSE_KEEPING]
      },
      'HOUSE_KEEPING_MODULE'
    ).then((response: UserUIModel[] | any) => {
      if (!isErrorInfo(response)) {
        const options = (response || []).map((user: UserUIModel) => ({
          value: user.id || '',
          label: user.name || ''
        }));

        setHousekeepingOptions(
          options.filter((option: HousekeepingAssigneeOption) => Boolean(option.value))
        );
      }
    });
  }, [canManageAssignments]);

  useEffect(() => {
    loadOrders(1);

    if (canViewFilters) {
      getAllProperties().then((res: PropertyUiModel[] | any) => {
        if (!isErrorInfo(res)) {
          setProjectOptions(
            (res || []).map((p: any) => ({
              label: p.name || '',
              value: p.id || '',
              areaId: p.areaId || ''
            }))
          );
        }
      });

      const searchQuery = {};
      getAllUnitTypes('HOUSE_KEEPING_MODULE', 1, 1000, searchQuery).then(
        (response: UnitTypeUiModelResponse | any) => {
          if (!isErrorInfo(response)) {
            setUnitOptions(
              (response?.unitList || [])
                .map((unitType: any) =>
                  (unitType?.units || []).map((u: any) => ({
                    label: u.shortName || '',
                    value: String(u.unitId || ''),
                    projectId: String(unitType.propertyId || '')
                  }))
                )
                .flat()
            );
          }
        }
      );
    }

    loadHousekeepingUsers();
  }, [canViewFilters, loadHousekeepingUsers]);

  const handleSearch = () => {
    setOffset(1);
    loadOrders(1);
  };

  const handlePrevPage = () => {
    if (offset === 1) {
      return;
    }

    const nextOffset = offset - 1;
    setOffset(nextOffset);
    loadOrders(nextOffset);
  };

  const handleNextPage = () => {
    const hasMore = offset * limit < count;
    if (!hasMore) {
      return;
    }

    const nextOffset = offset + 1;
    setOffset(nextOffset);
    loadOrders(nextOffset);
  };

  const handleAssigneesChange = (orderId: string, userIds: string[]) => {
    setSelectedAssigneesByOrder((current) => ({
      ...current,
      [orderId]: userIds
    }));
  };

  const openAssignModal = (order: HousekeepingOrderItem) => {
    setSelectedAssigneesByOrder((current) => ({
      ...current,
      [order.id]: current[order.id] || []
    }));
    setAssignmentOrder(order);
  };

  const closeAssignModal = () => setAssignmentOrder(null);

  const closeBulkAssignModal = () => {
    setBulkAssignOpen(false);
    setBulkAssignProject('');
    setBulkAssignUnits([]);
    setBulkAssignUserId('');
  };

  const isSingleAssignMode = Boolean(assignmentOrder);
  const assignModalOpen = isSingleAssignMode || bulkAssignOpen;
  const assignModalMode = isSingleAssignMode ? 'single' : 'bulk';
  const assignModalSelectedUserIds = isSingleAssignMode
    ? selectedAssigneesByOrder[assignmentOrder?.id || ''] || []
    : bulkAssignUserId
    ? [bulkAssignUserId]
    : [];
  const assignModalSaving = isSingleAssignMode
    ? Boolean(savingAssignmentsByOrder[assignmentOrder?.id || ''])
    : bulkAssignSaving;

  const handleAssignModalProjectChange = (projectValue: string) => {
    const normalizedProjectValue = String(projectValue || '');

    setBulkAssignProject(normalizedProjectValue);
    setBulkAssignUnits((current) =>
      current.filter((unitId) =>
        unitOptions.some(
          (option) =>
            option.value === unitId &&
            (!normalizedProjectValue || String(option.projectId || '') === normalizedProjectValue)
        )
      )
    );
  };

  const handleAssignModalUsersChange = (userIds: string[]) => {
    if (assignmentOrder) {
      handleAssigneesChange(assignmentOrder.id, userIds);
      return;
    }

    setBulkAssignUserId(userIds[0] || '');
  };

  const handleAssignModalClose = () => {
    if (assignmentOrder) {
      closeAssignModal();
      return;
    }

    closeBulkAssignModal();
  };

  const handleAssignModalSubmit = async () => {
    if (assignmentOrder) {
      await handleAssignUsers(assignmentOrder);
      return;
    }

    await handleBulkAssignUsers();
  };

  const handleAssignUsers = (order: HousekeepingOrderItem) => {
    const selectedUserIds = selectedAssigneesByOrder[order.id] || [];
    const users = selectedUserIds
      .map((userId) => housekeepingOptionsById[userId])
      .filter(Boolean)
      .map((user) => user.value)
      .filter(Boolean);

    setSavingAssignmentsByOrder((current) => ({ ...current, [order.id]: true }));

    const promise = assignHousekeepingOrderUsers(order.id, {
      assignedToId: users[0]
    }).then((response) => {
      if (isErrorInfo(response)) {
        throw response;
      }

      setOrders((current) => current.map((item) => (item.id === order.id ? response : item)));
      setSelectedAssigneesByOrder((current) => ({
        ...current,
        [order.id]: users[0] ? [users[0]] : []
      }));
      closeAssignModal();
      return response;
    });

    birdToast.promise(promise, {
      loadingMessage: 'جاري حفظ تعيين فريق النظافة...',
      successMessage: 'تم حفظ تعيين فريق النظافة بنجاح',
      errorFailedMessage: 'حدث خطأ أثناء حفظ التعيين. حاول مرة أخرى.'
    });
    setSavingAssignmentsByOrder((current) => ({ ...current, [order.id]: false }));
  };

  const handleBulkAssignUsers = async () => {
    const hasProject = Boolean(bulkAssignProject);
    const hasUnits = bulkAssignUnits?.length > 0;
    const hasUsers = Boolean(bulkAssignUserId);

    if ((!hasProject && !hasUnits) || !hasUsers) {
      birdToast.error(
        'يرجى اختيار موظف نظافة واحد على الأقل بالإضافة إلى مشروع أو وحدة واحدة على الأقل.'
      );
      return;
    }

    const payload = {
      ...(hasUsers ? { assignedToId: bulkAssignUserId } : {}),
      ...(hasUnits ? { unitTypeIds: bulkAssignUnits } : {}),
      ...(!hasUnits && hasProject ? { projectId: bulkAssignProject } : {})
    };

    setBulkAssignSaving(true);

    const promise = assignHousekeepingUsers(payload).then((response) => {
      if (isErrorInfo(response)) {
        throw response;
      }

      closeBulkAssignModal();
      setOffset(1);
      loadOrders(1);
      return response;
    });

    try {
      birdToast.promise(promise, {
        loadingMessage: 'جاري حفظ تعيين فريق النظافة...',
        successMessage: 'تم حفظ تعيين فريق النظافة بنجاح',
        errorFailedMessage: 'حدث خطأ أثناء حفظ التعيين. حاول مرة أخرى.'
      });
    } finally {
      setBulkAssignSaving(false);
    }
  };

  return (
    <section
      className="min-h-screen bg-slate-50 px-3 py-4 sm:px-6 sm:py-6 lg:px-8"
      dir="rtl"
      lang="ar"
    >
      <div className="mx-auto space-y-4">
        <div className="text-right flex flex-row gap-10 items-center">
          <p className="text-4xl font-recoleta font-semibold text-neutral-500">وحدة النظافة</p>
          {canManageAssignments && (
            <div className="flex flex-row gap-4">
              <button
                type="button"
                onClick={() => setCreateOrderOpen(true)}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-xl font-semibold text-white"
              >
                إضافة طلب نظافة
              </button>

              <button
                type="button"
                onClick={() => setBulkAssignOpen(true)}
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-xl font-semibold text-emerald-700"
              >
                تعيين فريق النظافة
              </button>
            </div>
          )}
        </div>

        {canViewFilters &&
        (count > 0 || project || unit || Boolean(orderDateFrom) || Boolean(orderDateTo)) ? (
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              <Select
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                options={projectOptions}
                placeholder="اختر المشروع"
                value={projectOptions.find((option) => option.value === project) ?? null}
                isClearable
                className="w-full"
                onChange={(
                  value: ValueType<{ value: string; label: string; areaId?: string }, false>
                ) => {
                  setProject(value ? value.value : '');
                }}
              />
              <Select
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                options={unitOptions}
                placeholder="اختر الوحدة"
                value={unitOptions.find((option) => option.value === unit) ?? null}
                isClearable
                className="w-full"
                onChange={(value: ValueType<{ value: string; label: string }, false>) => {
                  setUnit(value ? value.value : '');
                }}
              />
              <ReactDatePicker
                selected={orderDateFrom}
                onChange={(date: Date | null) => setOrderDateFrom(date)}
                placeholderText="من تاريخ الطلب"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-emerald-500"
                dateFormat="yyyy-MM-dd"
                isClearable
              />
              <ReactDatePicker
                selected={orderDateTo}
                onChange={(date: Date | null) => setOrderDateTo(date)}
                placeholderText="إلى تاريخ الطلب"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-emerald-500"
                dateFormat="yyyy-MM-dd"
                isClearable
              />
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleSearch}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-xl font-semibold text-white"
                >
                  بحث
                </button>
              </div>

              <p className="text-sm text-slate-500">النتائج: {count}</p>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <p className="text-sm text-slate-500">جاري تحميل طلبات النظافة...</p>
          </div>
        ) : loadError ? (
          <div className="rounded-2xl border border-rose-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-2xl">
              ⚠️
            </div>
            <h2 className="mt-4 text-xl font-bold text-slate-900">تعذر تحميل طلبات النظافة</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500">
              حدثت مشكلة أثناء جلب البيانات. أعد المحاولة لعرض الطلبات ومتابعة أعمال النظافة.
            </p>
            <button
              type="button"
              onClick={() => loadOrders(offset)}
              className="mt-5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
            >
              إعادة المحاولة
            </button>
          </div>
        ) : orders.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              {orders.map((order) => (
                <HousekeepingOrderCardCompact
                  key={order.id}
                  unitName={order.unitName}
                  projectName={order.projectName}
                  unitTypeGroup={order.unitTypeGroup}
                  unitShortName={order.unitShortName}
                  creationMode="auto"
                  status={order.status || 'new'}
                  orderDate={order.orderDate}
                  createdAt={order.createdAt}
                  updatedAt={order.updatedAt}
                  assigneeName={order.assigneeName}
                  canManageAssignments={canManageAssignments}
                  onPrimaryAction={() => handleOrderUpdate(order)}
                  onAssignSelf={() => handleAssignSelf(order.id)}
                  onToggle={() => handleToggleCard(order.id)}
                  isExpanded={expandedId === order.id}
                  onOpenAssignModal={() => openAssignModal(order)}
                />
              ))}
            </div>

            <div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white p-4 shadow-sm gap-10">
              <button
                type="button"
                onClick={handlePrevPage}
                disabled={offset === 1}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                السابق
              </button>

              <p className="text-sm text-slate-500">
                الصفحة {offset} / {Math.max(1, Math.ceil(count / limit))}
              </p>

              <button
                type="button"
                onClick={handleNextPage}
                disabled={offset * limit >= count}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                التالي
              </button>
            </div>
          </>
        ) : project || unit || Boolean(orderDateFrom) || Boolean(orderDateTo) ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-2xl">
              🔎
            </div>
            <h2 className="mt-4 text-xl font-bold text-slate-900">لا توجد نتائج مطابقة</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500">
              لم نعثر على طلبات نظافة بهذه المعايير. جرّب توسيع البحث أو إزالة بعض الفلاتر للوصول
              إلى الطلبات المناسبة.
            </p>
            <button
              type="button"
              onClick={() => {
                setProject('');
                setUnit('');
                setOrderDateFrom(null);
                setOrderDateTo(null);
                setOffset(1);
                loadOrders(1, '', '');
              }}
              className="mt-5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
            >
              مسح الفلاتر
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-emerald-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-2xl">
              🧹
            </div>
            <h2 className="mt-4 text-xl font-bold text-slate-900">لا توجد طلبات نظافة حالياً</h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              ستظهر هنا طلبات النظافة الخاصة بالوحدات والمشاريع لتساعد الفريق على متابعة العمل، بدء
              التنفيذ، ومراجعة حالة كل طلب بسرعة.
            </p>
            <div className="mx-auto mt-5 max-w-md rounded-xl bg-slate-50 p-4 text-right">
              <p className="text-xs text-slate-400">عند توفر الطلبات ستتمكن من</p>
              <ul className="mt-2 space-y-2 text-sm text-slate-600">
                <li>• معرفة الوحدة والمشروع المرتبطين بكل طلب</li>
                <li>• متابعة حالة الطلب وتاريخ إنشائه</li>
                <li>• تنفيذ الإجراء الأساسي بسرعة من نفس البطاقة</li>
              </ul>
            </div>
          </div>
        )}

        <HouseKeepingAssignModal
          open={assignModalOpen}
          mode={assignModalMode}
          order={assignmentOrder}
          projectOptions={bulkProjectOptions}
          unitOptions={bulkUnitOptions}
          selectedProject={bulkAssignProject}
          selectedUnits={bulkAssignUnits}
          options={housekeepingOptions}
          selectedUserIds={assignModalSelectedUserIds}
          saving={assignModalSaving}
          onProjectChange={handleAssignModalProjectChange}
          onUnitsChange={setBulkAssignUnits}
          onChange={handleAssignModalUsersChange}
          onClose={handleAssignModalClose}
          onSubmit={handleAssignModalSubmit}
        />

        <HouseKeepingCreateModal
          open={createOrderOpen}
          projectOptions={projectOptions as any}
          unitTypeOptions={unitOptions as any}
          userOptions={housekeepingOptions}
          onClose={() => setCreateOrderOpen(false)}
          onCreated={() => {
            setOffset(1);
            loadOrders(1);
          }}
        />

        <HouseKeepingInspectionModal
          inspectionModal={inspectionModal}
          onPhotoChange={handleInspectionPhotoChange}
          onSubmit={handleConfirmDone}
          onCancel={() => setInspectionModal(null)}
        />
      </div>
    </section>
  );
};

export default HouseKeepingContainer;
